/**
 * Vocabulário da linha do tempo de um grupo — o que cada fato SIGNIFICA para quem opera.
 *
 * Separado da tela pelo mesmo motivo de `group-ui.ts`: o teste afirma sobre os rótulos sem
 * montar a página, e cabeçalho, lista e legenda usam o mesmo texto.
 *
 * Nada aqui decide regra nem soma nada. O servidor já mandou o tipo do evento, a causa da
 * saída, a origem do fato, a confiança do horário e o vínculo da pessoa com o grupo. Isto é
 * **tradução** (invariantes 3 e 4).
 *
 * As três armadilhas deste dado, e o que este arquivo faz com cada uma:
 *
 *  1. **`source`** separa o que foi visto ao vivo do que foi descoberto na conferência
 *     (ADR-0014). Um `SYNC` não é "aconteceu agora" — é "percebemos agora que tinha
 *     acontecido". Dar a mesma cara aos dois faz o produto mentir sobre a própria precisão.
 *  2. **`occurredAtSource`** diz se o horário é do WhatsApp ou o da nossa descoberta. São
 *     precisões diferentes e não podem ser apresentadas como iguais (invariante 9).
 *  3. **`joined_at` nulo** não é zero dia de permanência: é ausência de medição
 *     (invariante 8). A tela diz "desde antes do monitoramento", nunca uma data inventada.
 */
import type { GroupActivity, TimelineEvent } from '../../lib/api';

export type TimelineEventType = TimelineEvent['eventType'];
export type TimelineExitCause = NonNullable<TimelineEvent['exitCause']>;
export type TimelineSource = TimelineEvent['source'];
export type TimelineTimeSource = TimelineEvent['occurredAtSource'];

export type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

/* -------------------------------------------------------------------------- */
/*  1. Causa da saída — três valores, e o terceiro NÃO é "saiu sozinho"        */
/* -------------------------------------------------------------------------- */

export interface ExitCauseSpec {
  label: string;
  tone: Tone;
  hint: string;
}

/**
 * Os três valores de `participant_exit_cause`, exatamente como o enum do banco os declara:
 * `self`, `removed_by_admin` e `unknown` (ADR-0005).
 *
 * `unknown` é o que este produto mais erraria se fosse descuidado. Ele **não** quer dizer
 * "saiu sozinho": quer dizer que o WhatsApp não nos contou quem executou a ação — o
 * `_data.Sender` não veio, ou veio num espaço de identificador que não dá para comparar com
 * o do participante (ver `gows-author.ts`). Pintar isso como saída voluntária inventaria
 * evasão que não houve, no número principal do produto; pintar como remoção inventaria
 * culpa de um admin que talvez nem tenha agido.
 *
 * Por isso `unknown` tem rótulo próprio, tom próprio (neutro — não é bom nem ruim, é
 * ausência de informação) e uma frase que diz *por que* não sabemos.
 */
export const EXIT_CAUSE: Record<TimelineExitCause, ExitCauseSpec> = {
  self: {
    label: 'Saiu por vontade própria',
    tone: 'warning',
    hint: 'A pessoa saiu do grupo sozinha. É este caso, e só este, que conta como evasão.',
  },
  removed_by_admin: {
    label: 'Removido por um admin',
    tone: 'danger',
    hint: 'Um administrador tirou esta pessoa do grupo. Não conta como evasão — não foi a campanha que a perdeu.',
  },
  unknown: {
    label: 'Saiu — não dá para saber como',
    tone: 'neutral',
    hint: 'O WhatsApp não disse quem executou a saída, então não dá para afirmar se a pessoa saiu sozinha ou foi removida. Não é "saiu sozinho": é ausência de informação, e por isso não entra na evasão.',
  },
};

/* -------------------------------------------------------------------------- */
/*  2. O que aconteceu                                                         */
/* -------------------------------------------------------------------------- */

export interface EventSpec {
  label: string;
  tone: Tone;
  hint: string;
}

/**
 * Os sete valores de `participant_event_type`. `LEAVE` é o único que não fecha sozinho —
 * quem termina a frase é a `exit_cause`, por isso ele tem um rótulo genérico aqui e
 * `eventLabel()` abaixo é quem monta o texto final.
 */
export const EVENT_TYPE: Record<TimelineEventType, EventSpec> = {
  JOIN: {
    label: 'Entrou no grupo',
    tone: 'success',
    hint: 'O WhatsApp avisou a entrada na hora em que ela aconteceu.',
  },
  LEAVE: {
    label: 'Saiu do grupo',
    tone: 'warning',
    hint: 'Uma saída. Como foi — por conta própria ou por remoção — está escrito na própria linha.',
  },
  PROMOTED: {
    label: 'Virou admin',
    tone: 'info',
    hint: 'Ganhou poderes de administrador no grupo.',
  },
  DEMOTED: {
    label: 'Deixou de ser admin',
    tone: 'info',
    hint: 'Perdeu os poderes de administrador no grupo.',
  },
  JOIN_REQUEST: {
    label: 'Pediu para entrar',
    tone: 'neutral',
    hint: 'Pedido de entrada em grupo com aprovação. Pedir não é entrar: só vira entrada quando um admin aprova.',
  },
  SYNC_DISCOVERED: {
    label: 'Entrou — percebido na conferência',
    tone: 'warning',
    hint: 'Esta pessoa apareceu na lista do grupo sem que o aviso de entrada tivesse chegado. Sabemos que ela entrou; não sabemos quando.',
  },
  SYNC_MISSING: {
    label: 'Saiu — percebido na conferência',
    tone: 'warning',
    hint: 'Esta pessoa sumiu da lista do grupo sem que o aviso de saída tivesse chegado. Sabemos que ela saiu; não sabemos quando nem como.',
  },
};

/** O texto final da linha. Só `LEAVE` depende da causa para ficar completo. */
export function eventLabel(event: Pick<TimelineEvent, 'eventType' | 'exitCause'>): string {
  if (event.eventType === 'LEAVE') {
    /**
     * `exitCause` nulo num `LEAVE` não deveria acontecer (a ingestão sempre preenche os
     * três valores), mas se acontecer o certo é cair em "não dá para saber" — nunca em
     * "saiu sozinho", que é a afirmação mais forte das três.
     */
    return EXIT_CAUSE[event.exitCause ?? 'unknown'].label;
  }
  return EVENT_TYPE[event.eventType].label;
}

export function eventTone(event: Pick<TimelineEvent, 'eventType' | 'exitCause'>): Tone {
  if (event.eventType === 'LEAVE') return EXIT_CAUSE[event.exitCause ?? 'unknown'].tone;
  return EVENT_TYPE[event.eventType].tone;
}

export function eventHint(event: Pick<TimelineEvent, 'eventType' | 'exitCause'>): string {
  if (event.eventType === 'LEAVE') return EXIT_CAUSE[event.exitCause ?? 'unknown'].hint;
  return EVENT_TYPE[event.eventType].hint;
}

/* -------------------------------------------------------------------------- */
/*  3. De onde veio o fato — ao vivo × conferência                             */
/* -------------------------------------------------------------------------- */

export interface SourceSpec {
  label: string;
  tone: Tone;
  hint: string;
}

/**
 * A distinção que o ADR-0014 criou e que esta tela existe para tornar visível.
 *
 * `WEBHOOK` é o WhatsApp nos avisando no instante em que a coisa aconteceu. `SYNC` é a
 * conferência de lista descobrindo, depois, que algo tinha acontecido sem aviso — e por
 * isso o horário de um `SYNC` é sempre o da conferência, nunca o do fato.
 *
 * Misturar os dois apagaria `count(source = 'SYNC')`, que é a medida de quanto webhook
 * estamos perdendo — e é o número que diz se dá para confiar no resto.
 */
export const EVENT_SOURCE: Record<TimelineSource, SourceSpec> = {
  WEBHOOK: {
    label: 'Visto ao vivo',
    tone: 'success',
    hint: 'O WhatsApp nos avisou no momento em que aconteceu.',
  },
  SYNC: {
    label: 'Visto na conferência',
    tone: 'warning',
    hint: 'O aviso do WhatsApp não chegou. Percebemos depois, comparando quem está no grupo com quem achávamos que estava — então o horário desta linha é o da conferência, não o do fato.',
  },
  MANUAL: {
    label: 'Lançado à mão',
    tone: 'info',
    hint: 'Alguém registrou este fato manualmente, sem passar pelo WhatsApp.',
  },
};

/* -------------------------------------------------------------------------- */
/*  4. Que horas são essas                                                     */
/* -------------------------------------------------------------------------- */

export interface TimeSourceSpec {
  /** Vai embaixo do horário, em letra menor. Curto de propósito: é uma linha por fato. */
  label: string;
  hint: string;
  /** `false` = o carimbo é o do fato. `true` = é o do nosso recebimento. */
  approximate: boolean;
}

/**
 * `occurred_at` e `occurred_at_source` andam juntos ou não significam nada.
 *
 * Em `provider`/`gateway` o carimbo é do fato. Em `receipt` o carimbo é da **nossa
 * descoberta** — e mostrar os dois com a mesma cara faria "saiu às 14h02" valer igual para
 * uma saída que o WhatsApp carimbou e para uma que nós só notamos naquela hora.
 */
export const TIME_SOURCE: Record<TimelineTimeSource, TimeSourceSpec> = {
  provider: {
    label: 'Hora do WhatsApp',
    hint: 'O carimbo veio do próprio WhatsApp: é a hora em que o fato aconteceu.',
    approximate: false,
  },
  gateway: {
    label: 'Hora do provedor',
    hint: 'O carimbo veio do provedor que intermedia o WhatsApp. É próximo da hora do fato, mas passou por uma fila.',
    approximate: false,
  },
  receipt: {
    label: 'Hora em que soubemos',
    hint: 'Não temos a hora do fato — só a hora em que percebemos. O fato pode ter acontecido bem antes.',
    approximate: true,
  },
};

export function timeIsApproximate(event: Pick<TimelineEvent, 'occurredAtSource'>): boolean {
  return TIME_SOURCE[event.occurredAtSource].approximate;
}

/* -------------------------------------------------------------------------- */
/*  5. "Está há X dias no grupo" NÃO EXISTE para a maioria das pessoas         */
/* -------------------------------------------------------------------------- */

/**
 * A frase que substitui a permanência quando `joined_at` é nulo.
 *
 * O ADR-0014 decidiu **não** carimbar `now()` em quem já estava no grupo antes do
 * monitorador entrar: isso inventaria uma entrada de hoje para quem está lá desde março e
 * jogaria a permanência mediana para o dia da varredura. O preço é que, para a maioria das
 * pessoas de um grupo antigo, "há X dias no grupo" simplesmente **não existe**.
 *
 * Nunca `0 dias`, nunca a data da varredura: as duas coisas seriam números com cara de
 * medição, e não há medição nenhuma.
 */
export const SINCE_BEFORE_MONITORING = 'No grupo desde antes do monitoramento';

export const SINCE_BEFORE_MONITORING_HINT =
  'Esta pessoa já estava no grupo quando o número monitorado entrou, então não existe data de entrada. Não é "entrou hoje" nem "zero dias": é uma medição que nunca houve.';

/** Saída sem entrada correspondente (ADR-0005) — nem vínculo há. */
export const WITHOUT_MEMBERSHIP = 'Sem registro de entrada neste grupo';

export const WITHOUT_MEMBERSHIP_HINT =
  'Chegou uma saída desta pessoa sem que existisse uma entrada dela por aqui. É a "saída órfã": ela sai do grupo sem descontar ninguém da contagem.';

export interface MembershipLine {
  label: string;
  hint: string;
  /** Data de entrada, quando existe. Quem formata é a tela (fuso do workspace). */
  joinedAt: string | null;
}

/**
 * A linha de contexto da pessoa, nos três estados que o servidor distingue.
 *
 * `membership === null` e `membership.joinedAt === null` são coisas diferentes, e é por
 * isso que o servidor devolve os dois separados: um é "não há vínculo", o outro é "há
 * vínculo e não há data".
 */
export function membershipLine(event: Pick<TimelineEvent, 'membership'>): MembershipLine {
  if (event.membership === null) {
    return { label: WITHOUT_MEMBERSHIP, hint: WITHOUT_MEMBERSHIP_HINT, joinedAt: null };
  }
  if (event.membership.joinedAt === null) {
    return { label: SINCE_BEFORE_MONITORING, hint: SINCE_BEFORE_MONITORING_HINT, joinedAt: null };
  }
  return {
    label: 'Entrou em',
    hint: 'Vimos esta entrada acontecer, então a data é medida — não estimada.',
    joinedAt: event.membership.joinedAt,
  };
}

/* -------------------------------------------------------------------------- */
/*  6. Como a pessoa aparece                                                   */
/* -------------------------------------------------------------------------- */

/**
 * O apelido da pessoa na tela.
 *
 * Não existe nome de exibição guardado: `participant_identities` tem `identity_hash`,
 * `phone_jid`, `lid` e `resolved_phone` — e mais nada (ADR-0004). O telefone é dado pessoal
 * em claro e não aparece numa linha do tempo que se olha em pé no celular.
 *
 * Então a tela mostra o apelido curto que o servidor derivou do hash: estável (a mesma
 * pessoa é sempre o mesmo apelido), pseudônimo e honesto. Inventar "Participante 1"
 * inventaria uma ordem que não existe.
 */
export function participantLabel(participant: TimelineEvent['participant']): string {
  return participant === null ? 'Pessoa não identificada' : `Pessoa ${participant.code}`;
}

export const PARTICIPANT_CODE_HINT =
  'O painel não guarda o nome de ninguém: guarda um código derivado com a chave do seu workspace. O mesmo código é sempre a mesma pessoa dentro deste workspace.';

/* -------------------------------------------------------------------------- */
/*  7. O resumo do cabeçalho                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Quanto do que sabemos veio de aviso ao vivo.
 *
 * `null` quando não há fato nenhum na janela — e `null` **não é 0%** (invariante 8): "não
 * perdemos nada" e "não aconteceu nada ainda" são notícias bem diferentes.
 */
export function liveShare(facts: GroupActivity['facts']): number | null {
  if (facts.total === 0) return null;
  return facts.bySource.WEBHOOK / facts.total;
}

/** A frase do resumo de conferência. Serve ao cabeçalho e ao teste. */
export function reconciledNote(facts: GroupActivity['facts']): string | null {
  const descobertos = facts.bySource.SYNC;
  if (descobertos === 0) return null;
  return descobertos === 1
    ? '1 destes fatos não chegou por aviso do WhatsApp: foi descoberto na conferência.'
    : `${descobertos} destes fatos não chegaram por aviso do WhatsApp: foram descobertos na conferência.`;
}

/**
 * A frase de composição do grupo — a que existe no lugar de "há X dias no grupo".
 *
 * `null` quando o grupo nunca foi varrido: ali `group_participants` conhece só quem passou
 * por um evento nosso, e um grupo de 550 pessoas com 2 linhas responderia "2 pessoas" com
 * toda a autoridade de um número.
 */
export function membershipNote(membership: GroupActivity['membership']): string | null {
  if (membership === null) return null;
  if (membership.inside === 0) return 'Ninguém no grupo agora.';
  if (membership.sinceBeforeMonitoring === 0) {
    return `${membership.inside} ${membership.inside === 1 ? 'pessoa' : 'pessoas'} no grupo, e vimos ${membership.inside === 1 ? 'ela entrar' : 'todas entrarem'}.`;
  }
  if (membership.withJoinDate === 0) {
    return `${membership.inside} ${membership.inside === 1 ? 'pessoa' : 'pessoas'} no grupo — nenhuma com data de entrada: já estavam lá antes do monitoramento.`;
  }
  return `${membership.inside} pessoas no grupo: ${membership.withJoinDate} que vimos entrar e ${membership.sinceBeforeMonitoring} que já estavam lá antes do monitoramento.`;
}
